const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const CAMPAIGN_ID_PATTERN = /^[a-z0-9][a-z0-9-]{2,63}$/
const SAFE_INTERNAL_PATH_PATTERN = /^\/(?!\/)[A-Za-z0-9/_-]*$/
const RELEASE_STAGES = new Set(['alpha', 'beta', 'ga'])
const RELEASE_STATUSES = new Set(['planned', 'active', 'delayed'])

export const RELEASE_CAMPAIGN_CACHE_TTL_MS = 5 * 60 * 1000
export const RELEASE_CAMPAIGN_REFRESH_BASE_MS = 5 * 60 * 1000
export const RELEASE_CAMPAIGN_REFRESH_MAX_JITTER_MS = 30 * 1000
export const DISABLED_RELEASE_CAMPAIGN = Object.freeze({ enabled: false })
export const RELEASE_CAMPAIGN_COPY_LIMITS = Object.freeze({
  compactLabel: 64,
  ctaLabel: 32,
  title: 120,
  status: 240,
  body: 500,
})

function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isPublicText(value, maxLength) {
  return (
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.length <= maxLength
  )
}

export function formatReleaseDate(value) {
  if (typeof value !== 'string') {
    return null
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)

  if (!match) {
    return null
  }

  const [, yearText, monthText, dayText] = match
  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)
  const date = new Date(Date.UTC(year, month - 1, day))

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null
  }

  return `${day} ${MONTH_NAMES[month - 1]} ${yearText}`
}

export function getReleaseCampaignRefreshDelay(randomFraction = Math.random()) {
  const boundedFraction = Number.isFinite(randomFraction)
    ? Math.min(1, Math.max(0, randomFraction))
    : 0

  return (
    RELEASE_CAMPAIGN_REFRESH_BASE_MS +
    Math.floor(boundedFraction * RELEASE_CAMPAIGN_REFRESH_MAX_JITTER_MS)
  )
}

export function normalizeReleaseCampaign(value) {
  if (!isRecord(value) || value.enabled !== true) {
    return DISABLED_RELEASE_CAMPAIGN
  }

  const isValid =
    typeof value.id === 'string' &&
    CAMPAIGN_ID_PATTERN.test(value.id) &&
    RELEASE_STAGES.has(value.stage) &&
    RELEASE_STATUSES.has(value.status) &&
    formatReleaseDate(value.releaseDate) !== null &&
    isRecord(value.banner) &&
    isPublicText(
      value.banner.label,
      RELEASE_CAMPAIGN_COPY_LIMITS.compactLabel,
    ) &&
    isPublicText(value.banner.message, RELEASE_CAMPAIGN_COPY_LIMITS.body) &&
    isRecord(value.statusCopy) &&
    isPublicText(
      value.statusCopy.plannedPrefix,
      RELEASE_CAMPAIGN_COPY_LIMITS.status,
    ) &&
    isPublicText(
      value.statusCopy.active,
      RELEASE_CAMPAIGN_COPY_LIMITS.status,
    ) &&
    isPublicText(
      value.statusCopy.delayed,
      RELEASE_CAMPAIGN_COPY_LIMITS.status,
    ) &&
    isRecord(value.primaryCta) &&
    isPublicText(
      value.primaryCta.label,
      RELEASE_CAMPAIGN_COPY_LIMITS.ctaLabel,
    ) &&
    typeof value.primaryCta.path === 'string' &&
    value.primaryCta.path.length <= 500 &&
    SAFE_INTERNAL_PATH_PATTERN.test(value.primaryCta.path) &&
    isRecord(value.signup) &&
    isPublicText(value.signup.title, RELEASE_CAMPAIGN_COPY_LIMITS.title) &&
    isPublicText(value.signup.description, RELEASE_CAMPAIGN_COPY_LIMITS.body) &&
    isPublicText(
      value.signup.submitLabel,
      RELEASE_CAMPAIGN_COPY_LIMITS.ctaLabel,
    ) &&
    isRecord(value.pendingApproval) &&
    isPublicText(
      value.pendingApproval.title,
      RELEASE_CAMPAIGN_COPY_LIMITS.title,
    ) &&
    isPublicText(
      value.pendingApproval.message,
      RELEASE_CAMPAIGN_COPY_LIMITS.body,
    )

  if (!isValid) {
    return DISABLED_RELEASE_CAMPAIGN
  }

  return {
    enabled: true,
    id: value.id,
    stage: value.stage,
    status: value.status,
    releaseDate: value.releaseDate,
    banner: {
      label: value.banner.label,
      message: value.banner.message,
    },
    statusCopy: {
      plannedPrefix: value.statusCopy.plannedPrefix,
      active: value.statusCopy.active,
      delayed: value.statusCopy.delayed,
    },
    primaryCta: {
      label: value.primaryCta.label,
      path: value.primaryCta.path,
    },
    signup: {
      title: value.signup.title,
      description: value.signup.description,
      submitLabel: value.signup.submitLabel,
    },
    pendingApproval: {
      title: value.pendingApproval.title,
      message: value.pendingApproval.message,
    },
  }
}
